import React from "react";

import Slider, { Handle } from "rc-slider";

import { APP_TOOLTIP_ID } from "@/src/constants/tooltip";

import COLORS from "../../../constants/colors";
import "../../../css/common/rc-slider.scss";
import { isDurationFilter } from "../../../utils/typePredicate";

import type { CSSProperties, Dispatch, JSX, SetStateAction } from "react";

import type { DurationFilter } from "../../../api/MediaAPI.types";

const HANDLE_STYLE: CSSProperties = {
  borderColor: "transparent",
  boxShadow: "0 0 0 1px var(--border)",
  width: 20,
  height: 20,
  marginTop: -7,
};

interface MediaLibraryRangeDurationProps {
  durationFilterList: number[];
  durationFilter: DurationFilter;
  setDurationFilter: Dispatch<SetStateAction<DurationFilter | undefined>>;
}

function MediaLibraryRangeDuration({
  durationFilterList,
  durationFilter,
  setDurationFilter,
}: MediaLibraryRangeDurationProps): JSX.Element {
  const { Range } = Slider;
  const marks = durationFilterList;

  const handleDurationFilterChange = (indexes: number[]): void => {
    const updatedDurationFilter = {
      min: indexes[0] && durationFilterList[indexes[0]],
      max: indexes[1] && durationFilterList[indexes[1]],
    };
    if (isDurationFilter(updatedDurationFilter)) {
      setDurationFilter({
        min: updatedDurationFilter.min,
        max: updatedDurationFilter.max,
      });
    }
  };

  const defaultDurationFilter = [
    durationFilterList.indexOf(durationFilter.min),
    durationFilterList.indexOf(durationFilter.max),
  ];

  return (
    <div className="border-input bg-card mb-2.5 rounded-[0.3125rem] border px-3.75">
      <div className="relative">
        <div className="py-3.75">
          <Range
            defaultValue={defaultDurationFilter}
            max={marks.length - 1}
            min={0}
            onAfterChange={handleDurationFilterChange}
            railStyle={{ height: 6 }}
            step={1}
            handle={({ value, dragging, index, ...restProps }): JSX.Element => (
              <Handle
                key={index}
                data-tooltip-content={`${marks[value]}min`}
                data-tooltip-id={APP_TOOLTIP_ID}
                data-tooltip-place="top"
                value={value}
                {...restProps} // eslint-disable-line react/jsx-props-no-spreading
                style={HANDLE_STYLE}
              />
            )}
            trackStyle={[
              { backgroundColor: COLORS.primary.DEFAULT, height: 6 },
            ]}
          />
        </div>

        <div className="relative h-8.75 w-full">
          {marks.map((mark, index) => (
            <div
              key={mark}
              className="absolute flex -translate-x-1/2 flex-col items-center"
              style={{
                left: `${(index / (marks.length - 1)) * 100}%`,
              }}
            >
              <div className="bg-status-neutral mb-[0.15625rem] h-[0.46875rem] w-px" />
              <div className="text-2xs text-muted-foreground">
                {index === marks.length - 1
                  ? `+${marks[marks.length - 1]}`
                  : mark}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default MediaLibraryRangeDuration;
